import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, StatCard, Loader, fmtDate } from "@/components/shared";
import {
  FileText, ClipboardCheck, Flag, Users, BarChart3, Layers, ScrollText, Activity, ArrowRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/shared";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";

const STATUS_COLORS = {
  draft: "#94a3b8", submitted: "#3b82f6", in_validation: "#f59e0b",
  validated: "#6366f1", follow_up_open: "#f43f5e", closed: "#10b981",
};

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/dashboard").then((r) => setData(r.data)).catch(() => setData({ stats: {} }));
  }, []);

  if (!data) return <Loader label="Loading dashboard" />;
  const s = data.stats;
  const role = user.role;

  const cards = {
    tac: [
      { label: "My Assessments", value: s.my_assessments ?? 0, icon: FileText, testid: "stat-my-assessments" },
      { label: "Drafts", value: s.drafts ?? 0, icon: Layers, testid: "stat-drafts" },
      { label: "In Validation", value: s.in_validation ?? 0, icon: ClipboardCheck, testid: "stat-in-validation" },
      { label: "Open Follow-ups", value: s.open_followups ?? 0, icon: Flag, accent: "text-rose-600", testid: "stat-open-followups" },
    ],
    validator: [
      { label: "Validation Queue", value: s.queue ?? 0, icon: ClipboardCheck, accent: "text-amber-600", testid: "stat-queue" },
      { label: "Completed", value: s.completed ?? 0, icon: BarChart3, testid: "stat-completed" },
      { label: "Open Follow-ups", value: s.open_followups ?? 0, icon: Flag, accent: "text-rose-600", testid: "stat-open-followups" },
      { label: "Created by Me", value: s.created_followups ?? 0, icon: Flag, testid: "stat-created-followups" },
    ],
    am: [
      { label: "Assessments", value: s.total_assessments ?? 0, icon: FileText, testid: "stat-total-assessments" },
      { label: "Submissions", value: s.submissions ?? 0, icon: Layers, testid: "stat-submissions" },
      { label: "Partners", value: s.partners ?? 0, icon: Users, testid: "stat-partners" },
      { label: "Open Follow-ups", value: s.open_followups ?? 0, icon: Flag, accent: "text-rose-600", testid: "stat-open-followups" },
    ],
    pd: [
      { label: "Assessments", value: s.total_assessments ?? 0, icon: FileText, testid: "stat-total-assessments" },
      { label: "Submissions", value: s.submissions ?? 0, icon: Layers, testid: "stat-submissions" },
      { label: "Partners", value: s.partners ?? 0, icon: Users, testid: "stat-partners" },
      { label: "Open Follow-ups", value: s.open_followups ?? 0, icon: Flag, accent: "text-rose-600", testid: "stat-open-followups" },
    ],
    admin: [
      { label: "Total Users", value: s.total_users ?? 0, icon: Users, testid: "stat-total-users" },
      { label: "Pending Approval", value: s.pending_users ?? 0, icon: Users, accent: "text-amber-600", testid: "stat-pending-users" },
      { label: "Assessments", value: s.total_assessments ?? 0, icon: FileText, testid: "stat-total-assessments" },
      { label: "Audit Events", value: s.audit_events ?? 0, icon: ScrollText, testid: "stat-audit-events" },
    ],
  }[role] || [];

  const dist = (s.status_distribution || []).map((d) => ({
    name: d.status.replace(/_/g, " "), value: d.count, key: d.status,
  }));

  return (
    <div>
      <PageHeader
        overline={`Signed in · ${role.toUpperCase()}`}
        title={`Welcome, ${data.name?.split(" ")[0] || "there"}`}
        description="Your role-aware overview of assessments, validation and governance follow-ups."
        actions={
          role === "tac" ? (
            <Button onClick={() => navigate("/assessments")} data-testid="dashboard-cta-button" className="rounded-md">
              Go to my assessments <ArrowRight className="h-4 w-4 ml-1.5" aria-hidden="true" />
            </Button>
          ) : role === "validator" ? (
            <Button onClick={() => navigate("/validation")} data-testid="dashboard-cta-button" className="rounded-md">
              Open validation queue <ArrowRight className="h-4 w-4 ml-1.5" aria-hidden="true" />
            </Button>
          ) : null
        }
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((c, i) => (
          <motion.div key={c.label} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}>
            <StatCard {...c} />
          </motion.div>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mt-6">
        {dist.length > 0 && (
          <div className="lg:col-span-2 bg-card border border-border rounded-md p-6">
            <div className="overline mb-4 flex items-center gap-2"><BarChart3 className="h-3.5 w-3.5" aria-hidden="true" /> Assessment status distribution</div>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={dist} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#64748b" }} axisLine={{ stroke: "#e2e8f0" }} tickLine={false} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} />
                <Tooltip cursor={{ fill: "#f1f5f9" }} contentStyle={{ borderRadius: 6, border: "1px solid #e2e8f0", fontSize: 12 }} />
                <Bar dataKey="value" radius={[3, 3, 0, 0]}>
                  {dist.map((d) => <Cell key={d.key} fill={STATUS_COLORS[d.key] || "#0f172a"} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {s.recent_activity && (
          <div className="bg-card border border-border rounded-md p-6">
            <div className="overline mb-4 flex items-center gap-2"><Activity className="h-3.5 w-3.5" aria-hidden="true" /> Recent activity</div>
            <ul className="space-y-3" data-testid="recent-activity">
              {s.recent_activity.map((a) => (
                <li key={a.id} className="text-sm border-l-2 border-slate-200 pl-3">
                  <div className="font-medium text-foreground">{a.action}</div>
                  <div className="text-xs text-muted-foreground font-mono">{a.actor_name} · {fmtDate(a.timestamp)}</div>
                </li>
              ))}
              {s.recent_activity.length === 0 && <li className="text-sm text-muted-foreground">No activity yet.</li>}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
